import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import NdaNetherlandsHero from './components/NdaNetherlandsHero';
import NdaNetherlandsContent from './components/NdaNetherlandsContent';
import NdaNetherlandsClauses from './components/NdaNetherlandsClauses';
import NdaNetherlandsFaq from './components/NdaNetherlandsFaq';
import NdaNetherlandsCta from './components/NdaNetherlandsCta';
import { faqs } from './components/NdaNetherlandsFaq';

const schemaData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': 'https://pactorae.com/nda-template-netherlands',
      url: 'https://pactorae.com/nda-template-netherlands',
      name: 'NDA Template voor ZZP\'ers in Nederland | Pactorae',
      description:
        'Gratis NDA template voor Nederlandse ZZP\'ers en freelancers. Professionele geheimhoudingsverklaring naar Nederlands recht. Inclusief KvK-nummer en btw-nummer.',
      inLanguage: 'nl',
      isPartOf: { '@type': 'WebSite', url: 'https://pactorae.com', name: 'Pactorae' },
    },
    {
      '@type': 'FAQPage',
      mainEntity: faqs?.map((faq) => ({
        '@type': 'Question',
        name: faq?.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq?.answer,
        },
      })),
    },
  ],
};

const NdaNetherlandsLandingPage = () => {
  return (
    <>
      <Helmet>
        <html lang="nl" />
        <title>NDA Template voor ZZP'ers in Nederland | Pactorae</title>
        <meta
          name="description"
          content="Gratis NDA template voor Nederlandse ZZP'ers en freelancers. Professionele geheimhoudingsverklaring naar Nederlands recht. Inclusief KvK-nummer en btw-nummer. Genereer direct online."
        />
        <meta
          name="keywords"
          content="NDA template Nederland, geheimhoudingsverklaring zzp, NDA freelancer Nederland, NDA zzp'er, geheimhouding overeenkomst Nederland, non disclosure agreement Nederland"
        />
        <link rel="canonical" href="https://pactorae.com/nda-template-netherlands" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pactorae.com/nda-template-netherlands" />
        <meta property="og:title" content="NDA Template voor ZZP'ers in Nederland | Pactorae" />
        <meta
          property="og:description"
          content="Gratis NDA template voor Nederlandse ZZP'ers en freelancers. Professionele geheimhoudingsverklaring naar Nederlands recht."
        />
        <meta property="og:site_name" content="Pactorae" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="NDA Template voor ZZP'ers in Nederland | Pactorae" />
        <meta
          name="twitter:description"
          content="Gratis NDA template voor Nederlandse ZZP'ers. Geheimhoudingsverklaring naar Nederlands recht met KvK-nummer."
        />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      <main>
        <NdaNetherlandsHero />

        <article className="max-w-4xl mx-auto px-6 py-16">
          {/* Intro */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-4">Wanneer heb je een NDA nodig als ZZP'er in Nederland?</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-4">
              Als ZZP'er of freelancer in Nederland kom je regelmatig in aanraking met vertrouwelijke informatie van opdrachtgevers. Een NDA (Non-Disclosure Agreement) of geheimhoudingsverklaring legt juridisch vast welke informatie vertrouwelijk is en wat de gevolgen zijn bij schending. In Nederland wordt de bescherming van bedrijfsgeheimen geregeld door de <strong>Wet bescherming bedrijfsgeheimen (Wbb)</strong>.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              Via Pactorae genereer je in enkele minuten een professionele NDA op maat voor jouw Nederlandse freelance opdracht. De generator vraagt naar jouw <strong>KvK-nummer</strong> en <strong>btw-nummer</strong> voor correcte identificatie van partijen, en past alle clausules aan op het Nederlandse recht.
            </p>
          </section>

          <NdaNetherlandsContent />
          <NdaNetherlandsClauses />
          <NdaNetherlandsFaq />
          <NdaNetherlandsCta />

          {/* Internal links */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-6">Gerelateerde contract templates</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/nda-template-belgium"
                className="block p-5 bg-white border border-gray-200 rounded-xl hover:border-[#FF8C00] hover:shadow-md transition-all duration-200"
              >
                <div className="text-[#FF8C00] font-semibold mb-1">NDA België →</div>
                <div className="text-gray-600 text-sm">NDA template voor Belgische freelancers en zelfstandigen</div>
              </Link>
              <Link
                to="/freelance-contract-netherlands"
                className="block p-5 bg-white border border-gray-200 rounded-xl hover:border-[#FF8C00] hover:shadow-md transition-all duration-200"
              >
                <div className="text-[#FF8C00] font-semibold mb-1">Freelance Contract Nederland →</div>
                <div className="text-gray-600 text-sm">Freelance contract template voor Nederlandse ZZP'ers</div>
              </Link>
              <Link
                to="/freelance-contract-template-benelux"
                className="block p-5 bg-white border border-gray-200 rounded-xl hover:border-[#FF8C00] hover:shadow-md transition-all duration-200"
              >
                <div className="text-[#FF8C00] font-semibold mb-1">Benelux Templates →</div>
                <div className="text-gray-600 text-sm">Contract generator voor België en Nederland</div>
              </Link>
            </div>
          </section>
        </article>
      </main>
    </>
  );
};

export default NdaNetherlandsLandingPage;
